/**
 * The page input of a dated reference (1.Y deliverable 2): a combobox labelled "Page" over
 * `search.picker`'s canon hits, 150 ms after the last keystroke. Choosing a hit hands it over;
 * nothing is written here.
 */
import type { DocHitV1 } from '@codex/contracts';
import { useLayer } from '@codex/ui';
import { useQuery } from '@tanstack/react-query';
import { type KeyboardEvent, useEffect, useId, useState } from 'react';
import { temporalKeys, useTemporalApi } from './api.js';
import { CHOOSE_PAGE, NO_PAGES, PAGE_LABEL } from './words.js';

export const PICK_DEBOUNCE_MS = 150;

export interface RefPickerProps {
  readonly chosen: DocHitV1 | null;
  readonly onChoose: (hit: DocHitV1 | null) => void;
  /** Pages that may not be chosen (this page, and those already linked). */
  readonly exclude: readonly string[];
}

export function RefPicker({ chosen, onChoose, exclude }: RefPickerProps) {
  const services = useTemporalApi();
  const id = useId();
  const listId = useId();
  const [text, setText] = useState(chosen?.title ?? '');
  const [settled, setSettled] = useState('');
  const [active, setActive] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(text.trim()), PICK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);
  const typing = chosen === null || text !== chosen.title;
  const query = useQuery({
    queryKey: temporalKeys.picker(settled),
    queryFn: () => services.searchCanon(settled),
    enabled: typing && settled !== '' && settled === text.trim(),
    retry: false,
  });
  const hits = (query.data ?? []).filter((hit) => !exclude.includes(hit.id));
  const listOpen = typing && !dismissed && settled !== '' && query.isSuccess;
  const current = Math.min(active, Math.max(hits.length - 1, 0));
  useLayer(listOpen, () => setDismissed(true), 'temporal-pages');

  const choose = (hit: DocHitV1 | undefined) => {
    if (hit === undefined) return;
    setText(hit.title);
    setDismissed(true);
    onChoose(hit);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!listOpen || hits.length === 0) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActive((current + step + hits.length) % hits.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(hits[current]);
    }
  };
  const optionId = (index: number) => `${listId}-${index}`;
  return (
    <div className="cx-field cx-temporal__picker">
      <label className="cx-field__label" htmlFor={id}>
        {PAGE_LABEL}
      </label>
      <span className="cx-input" data-control-boundary="">
        <input
          id={id}
          type="text"
          role="combobox"
          className="cx-input__el"
          data-testid="temporal.picker.field"
          aria-autocomplete="list"
          aria-expanded={listOpen && hits.length > 0}
          aria-controls={listId}
          aria-activedescendant={listOpen && hits.length > 0 ? optionId(current) : undefined}
          autoComplete="off"
          value={text}
          onChange={(event) => {
            setText(event.currentTarget.value);
            setDismissed(false);
            setActive(0);
            if (chosen !== null) onChoose(null);
          }}
          onKeyDown={onKeyDown}
        />
      </span>
      <div
        id={listId}
        role="listbox"
        aria-label="Pages"
        className="cx-when-input__list"
        hidden={!listOpen || hits.length === 0}
      >
        {hits.map((hit, index) => (
          // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox owns the keyboard (aria-activedescendant)
          <div
            key={hit.id}
            id={optionId(index)}
            role="option"
            tabIndex={-1}
            aria-selected={index === current}
            className="cx-when-input__option"
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => choose(hit)}
          >
            {hit.title}
          </div>
        ))}
      </div>
      {listOpen && hits.length === 0 && <span className="cx-field__description">{NO_PAGES}</span>}
      {typing && !listOpen && text.trim() === '' && (
        <span className="cx-field__description">{CHOOSE_PAGE}</span>
      )}
    </div>
  );
}
